// This module provides API calls to the Flask backend for orgs data
import axios from "axios";
import Response from "../response";
import { errCodes } from "../response/errors";
import { BLITZ_CI_AUTH, COMMISSIONS_API_BASE_URL } from "../../utils/constants";

export const defaultOrg = {
  id: "",
  name: "",
  is_internal: false,
};

class Orgs {
  constructor() {
    this.baseUrl = COMMISSIONS_API_BASE_URL;
    this.endpoint = "data/orgs";
    this.url = `${this.baseUrl}/${this.endpoint}`;
    this.headers = {
      'Content-Type': 'application/json',
      'Authorization': '',
    };
  }

  #checkSession() {
    this.accessToken = sessionStorage.getItem(BLITZ_CI_AUTH);
    this.headers['Authorization'] = `Bearer ${this.accessToken}`;
  }

  async getAllOrgs() {
    this.#checkSession();
    try {
      const response = await axios.get(`${this.url}/`, { headers: this.headers });
      if (response.status === 200 && Array.isArray(response.data)) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.NotFound);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async getOrgData(orgID) {
    this.#checkSession();
    try {
      const response = await axios.get(`${this.url}/${orgID}`, { headers: this.headers });
      if (response.status === 200 && response.data.id) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.NotFound);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async getOrgCarriers(orgID) {
    this.#checkSession();
    try {
      const response = await axios.get(`${this.url}/${orgID}/carriers`, { headers: this.headers });
      if (response.status === 200 && Array.isArray(response.data)) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.NotFound);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async getOrgFilesByCarrierID(orgID, carrierID) {
    this.#checkSession();
    try {
      const response = await axios.get(`${this.url}/${orgID}/files/${carrierID}`, { headers: this.headers });
      if (response.status === 200 && Array.isArray(response.data)) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.NotFound);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async getOrgSitesCredentials(orgID, carrierID) {
    this.#checkSession();
    try {
      const response = await axios.get(`${this.url}/${orgID}/sites/${carrierID}/credentials`, { headers: this.headers });
      if (response.status === 200 && Array.isArray(response.data)) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.NotFound);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async addOrgSiteCredential(orgID, siteID, credentialData) {
    this.#checkSession();
    try {
      const response = await axios.post(`${this.url}/${orgID}/sites/${siteID}/credentials`, credentialData, { headers: this.headers });
      if ((response.status === 201 || response.status === 200) && response.data.id) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.Internal);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async updateOrgSiteCredential(orgID, siteID, credentialID, credentialData) {
    this.#checkSession();
    try {
      const response = await axios.put(`${this.url}/${orgID}/sites/${siteID}/credentials/${credentialID}`, credentialData, { headers: this.headers });
      if ((response.status === 200 || response.status === 201) && response.data.id) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.Internal);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async getOrgSiteServiceRecords(orgID, carrierID, service) {
    this.#checkSession();
    try {
      const response = await axios.get(`${this.url}/${orgID}/sites/${carrierID}/services/${service}/records`, { headers: this.headers });
      if (response.status === 200 && Array.isArray(response.data)) {
        return Response.create(response.data, true);
      }
      return Response.create({}, false, {}, errCodes.NotFound);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }

  async deleteOrgSiteCredential(orgID, siteID, credentialID) {
    this.#checkSession();
    try {
      const response = await axios.delete(`${this.url}/${orgID}/sites/${siteID}/credentials/${credentialID}`, { headers: this.headers });
      if (response.status === 200) {
        return Response.create({ id: credentialID }, true);
      }
      return Response.create({}, false, {}, errCodes.Internal);
    } catch (error) {
      return Response.create({}, false, error, errCodes.Internal);
    }
  }
}

export default Orgs;
